<template>
    <div class="image-upload">
        <div class="image-upload__label text-caption">
            {{ label }}<span v-if="required" class="red--text"> *</span>
        </div>
        <div
            class="image-upload__frame"
            :class="{ 'image-upload__frame--empty': !hasPreview, 'image-upload__frame--readonly': readonly }"
            :style="{ height: `${height}px` }"
            @click="!hasPreview && pick()"
        >
            <template v-if="hasPreview">
                <v-img :src="src" :height="height" contain class="image-upload__preview" />
                <div v-if="!readonly" class="image-upload__overlay">
                    <v-btn small depressed color="white" @click.stop="pick">
                        <v-icon left small>mdi-swap-horizontal</v-icon>Change
                    </v-btn>
                    <v-btn small depressed color="red darken-1" class="white--text" @click.stop="remove">
                        <v-icon left small>mdi-delete-outline</v-icon>Remove
                    </v-btn>
                </div>
            </template>
            <div v-else class="image-upload__placeholder">
                <v-icon large color="grey lighten-1">mdi-image-plus</v-icon>
                <span class="text-body-2 mt-2">{{ readonly ? 'No image' : 'Click to upload' }}</span>
                <span v-if="hint && !readonly" class="text-caption grey--text">{{ hint }}</span>
            </div>
        </div>
        <div v-if="hasPreview && fileName" class="image-upload__meta text-caption grey--text">
            {{ fileName }}
        </div>
        <input ref="input" type="file" :accept="accept" hidden @change="onFileSelected">
    </div>
</template>

<script>
    // Drop-in replacement for the v-file-input + v-img preview pair used in the forms:
    // v-model is the selected File, `src` the preview url, and it emits the same change / clear events.
    export default {
        name: 'ImageUpload',
        props: {
            value: {
                type: [Object, File, String],
                default: null
            },
            src: {
                type: String,
                default: ''
            },
            label: {
                type: String,
                default: 'Image'
            },
            hint: {
                type: String,
                default: null
            },
            accept: {
                type: String,
                default: 'image/*'
            },
            height: {
                type: [Number, String],
                default: 200
            },
            readonly: {
                type: Boolean,
                default: false
            },
            required: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            hasPreview() {
                return !!(this.value && this.src)
            },
            fileName() {
                return this.value instanceof File ? `${this.value.name} (${Math.round(this.value.size / 1024)} KB)` : null
            }
        },
        methods: {
            pick() {
                if (!this.readonly) {
                    this.$refs.input.click()
                }
            },
            onFileSelected(event) {
                const file = event.target.files[0]
                event.target.value = ''
                if (file) {
                    this.$emit('input', file)
                    this.$emit('change', file)
                }
            },
            remove() {
                this.$emit('input', null)
                this.$emit('change', null)
                this.$emit('clear')
            }
        }
    }
</script>

<style scoped>
    .image-upload__label {
        color: rgba(0, 0, 0, 0.6);
        margin-bottom: 4px;
    }

    .image-upload__frame {
        position: relative;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 8px;
        overflow: hidden;
        background: repeating-conic-gradient(#f5f5f5 0% 25%, #fff 0% 50%) 50% / 16px 16px;
    }

    .image-upload__frame--empty {
        border-style: dashed;
        border-width: 2px;
        background: #fafafa;
        cursor: pointer;
        transition: border-color 0.2s, background 0.2s;
    }

    .image-upload__frame--empty:not(.image-upload__frame--readonly):hover {
        border-color: #009688;
        background: #f0faf8;
    }

    .image-upload__frame--readonly {
        cursor: default;
    }

    .image-upload__placeholder {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: rgba(0, 0, 0, 0.6);
    }

    .image-upload__overlay {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        background: rgba(0, 0, 0, 0.45);
        opacity: 0;
        transition: opacity 0.2s;
    }

    .image-upload__frame:hover .image-upload__overlay {
        opacity: 1;
    }

    .image-upload__meta {
        margin-top: 4px;
    }
</style>
